import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import InternalMenu from '../../components/shared/InternalMenu';
import Button from '../../components/Button';

const AppAnalysisDetailsView = ({ appDetails, loading, error }) => {
  // Dashboard menu items with icons
  const menuItems = [
    { 
      path: '/dashboard', 
      label: 'Dashboard',
      icon: <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
        />
      </svg>
    },
    { 
      path: '/new-project', 
      label: 'New Project',
      icon: <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex w-full">
        <InternalMenu 
          items={menuItems} 
          variant="dashboard" 
          showUserProfile={true} 
        />
        <div className="flex-1 md:ml-64 p-8">
          <div className="bg-red-900/30 border border-red-700 text-white p-6 rounded-lg">
            <h2 className="text-xl font-bold mb-4">Analysis Not Available</h2>
            <p className="mb-4">{error}</p>
            <div className="mt-6 space-y-4">
              <h3 className="text-lg font-semibold">Options:</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li>Check if the analysis is complete - it may still be processing</li>
                <li>Verify that the file ID is correct</li>
                <li>Try uploading the application again for a new analysis</li>
              </ul>
            </div>
            <div className="mt-6 flex space-x-4">
              <Link to="/dashboard" className="inline-block">
                <Button className="bg-purple-600 hover:bg-purple-700">Back to Dashboard</Button>
              </Link>
              <Link to="/new-project" className="inline-block">
                <Button className="bg-indigo-600 hover:bg-indigo-700">Upload New Application</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!appDetails) {
    return (
      <div className="flex w-full">
        <InternalMenu 
          items={menuItems} 
          variant="dashboard" 
          showUserProfile={true} 
        />
        <div className="flex-1 md:ml-64 p-8">
          <div className="bg-gray-800 text-white p-6 rounded-lg">
            <h2 className="text-xl font-bold mb-2">No Data Available</h2>
            <p>Could not find details for this application.</p>
            <Link to="/dashboard" className="mt-4 inline-block">
              <Button className="mt-4">Back to Dashboard</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Format timestamp to readable format
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    if (typeof timestamp === 'object' && timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString();
    }
    
    return new Date(timestamp).toLocaleString();
  };

  // Format file size
  const formatFileSize = (sizeString) => {
    return sizeString || 'N/A';
  };

  // Get severity class for findings badges
  const getSeverityClass = (severity) => {
    switch(severity) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'info':
        return 'bg-blue-100 text-blue-800';
      case 'secure':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="flex w-full">
      <InternalMenu 
        items={menuItems} 
        variant="dashboard" 
        showUserProfile={true} 
      />
      
      {/* Main Content */}
      <div className="flex-1 md:ml-64 w-auto">
        <div className="bg-gray-900 text-white min-h-screen w-full">
          {/* Hero Banner */}
          <div className="bg-gradient-to-r from-purple-700 to-indigo-900 py-12 w-full">
            <div className="max-w-6xl mx-auto px-4 md:px-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div>
                  <Link to="/dashboard" className="text-white/80 hover:text-white flex items-center text-sm mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Dashboard
                  </Link>
                  <h1 className="text-3xl md:text-4xl font-bold">
                    {appDetails.report_summary?.app_name || appDetails.file_name || 'App Details'}
                  </h1>
                  <p className="text-lg opacity-80 mt-2">
                    {appDetails.report_summary?.package_name || 'Package details'}
                  </p>
                </div>
                <div className="mt-4 md:mt-0">
                  <div className="text-right">
                    <div className="text-sm text-white/80">Security Score</div>
                    <div className={`text-3xl font-bold ${appDetails.report_summary?.security_score >= 70 ? 'text-green-400' : appDetails.report_summary?.security_score >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {appDetails.report_summary?.security_score || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* App Details Content */}
          <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Summary Card */}
              <div className="bg-gray-800 rounded-lg p-6 shadow-lg col-span-full md:col-span-2">
                <h2 className="text-xl font-bold mb-4">Application Summary</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-gray-400 text-sm">App Name</div>
                    <div className="font-medium">{appDetails.report_summary?.app_name || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm">Package Name</div>
                    <div className="font-medium">{appDetails.report_summary?.package_name || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm">Version</div>
                    <div className="font-medium">{`${appDetails.report_summary?.version_name || 'N/A'} (${appDetails.report_summary?.version_code || 'N/A'})`}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm">File Size</div>
                    <div className="font-medium">{formatFileSize(appDetails.report_summary?.file_size)}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm">Scan Started</div>
                    <div className="font-medium">{formatDate(appDetails.scan_started_at)}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm">Scan Completed</div>
                    <div className="font-medium">{formatDate(appDetails.completed_at)}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm">Scan Duration</div>
                    <div className="font-medium">{appDetails.scan_duration ? `${appDetails.scan_duration.toFixed(2)}s` : 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm">File Hash</div>
                    <div className="font-medium text-xs break-all">{appDetails.hash || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {/* Status Card */}
              <div className="bg-gray-800 rounded-lg p-6 shadow-lg">
                <h2 className="text-xl font-bold mb-4">Scan Status</h2>
                <div className="mb-4">
                  <div className="text-gray-400 text-sm">Current Status</div>
                  <div className="font-medium flex items-center mt-1">
                    <span className={`inline-block w-3 h-3 rounded-full mr-2 ${appDetails.status === 'completed' ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                    {appDetails.current_step || appDetails.status || 'N/A'}
                  </div>
                </div>
                <div className="mb-4">
                  <div className="text-gray-400 text-sm">Last Updated</div>
                  <div className="font-medium">{formatDate(appDetails.last_updated)}</div>
                </div>
                {appDetails.report_url && (
                  <a href={appDetails.report_url} target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 text-sm flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    View Full Analysis Report
                  </a>
                )}
              </div>
            </div>

            {/* Findings Summary */}
            {appDetails.report_summary?.findings && (
              <div className="bg-gray-800 rounded-lg p-6 shadow-lg mb-8">
                <h2 className="text-xl font-bold mb-4">Security Findings</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-gray-700 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-red-400">{appDetails.report_summary.findings.high || 0}</div>
                    <div className="text-sm text-gray-300">Critical Issues</div>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-yellow-400">{appDetails.report_summary.findings.warning || 0}</div>
                    <div className="text-sm text-gray-300">Warnings</div>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-blue-400">{appDetails.report_summary.findings.info || 0}</div>
                    <div className="text-sm text-gray-300">Info</div>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-green-400">{appDetails.report_summary.findings.secure || 0}</div>
                    <div className="text-sm text-gray-300">Secure</div>
                  </div>
                </div>
              </div>
            )}

            {/* Permissions */}
            {appDetails.report_summary?.permissions && (
              <div className="bg-gray-800 rounded-lg p-6 shadow-lg mb-8">
                <h2 className="text-xl font-bold mb-6">Permissions</h2>
                
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Normal Permissions</h3>
                    <span className="px-3 py-1 text-sm bg-blue-900 text-blue-300 rounded-full">
                      {appDetails.report_summary.permissions.normal ? appDetails.report_summary.permissions.normal.length : 0} permissions
                    </span>
                  </div>
                  {appDetails.report_summary.permissions.normal && appDetails.report_summary.permissions.normal.length > 0 ? (
                    <div className="grid grid-cols-1 gap-3">
                      {appDetails.report_summary.permissions.normal.map((perm, index) => (
                        <div key={index} className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                          <div className="flex items-start">
                            <div className="bg-blue-900 p-2 rounded mr-3">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                              </svg>
                            </div>
                            <div>
                              <div className="font-medium">{perm.name}</div>
                              <div className="text-sm text-gray-400 mt-1">{perm.info || perm.description}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-800 p-4 rounded-lg text-center text-gray-400 border border-gray-700">
                      No normal permissions required
                    </div>
                  )}
                </div>

                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Dangerous Permissions</h3>
                    <span className="px-3 py-1 text-sm bg-red-900 text-red-300 rounded-full">
                      {appDetails.report_summary.permissions.dangerous ? appDetails.report_summary.permissions.dangerous.length : 0} permissions
                    </span>
                  </div>
                  {appDetails.report_summary.permissions.dangerous && appDetails.report_summary.permissions.dangerous.length > 0 ? (
                    <div className="grid grid-cols-1 gap-3">
                      {appDetails.report_summary.permissions.dangerous.map((perm, index) => (
                        <div key={index} className="bg-gray-800 p-4 rounded-lg border border-red-900">
                          <div className="flex items-start">
                            <div className="bg-red-900 p-2 rounded mr-3">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                            </div>
                            <div>
                              <div className="font-medium">{perm.name}</div>
                              <div className="text-sm text-gray-400 mt-1">{perm.info || perm.description}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-800 p-4 rounded-lg border border-green-800">
                      <div className="flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-green-400 font-medium">No dangerous permissions required</span>
                      </div>
                      <div className="text-center text-sm text-gray-400 mt-2">
                        This application doesn't request any sensitive permissions
                      </div>
                    </div>
                  )}
                </div>

                {appDetails.report_summary.permissions.unknown && appDetails.report_summary.permissions.unknown.length > 0 && (
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Unknown Permissions</h3>
                      <span className="px-3 py-1 text-sm bg-purple-900 text-purple-300 rounded-full">
                        {appDetails.report_summary.permissions.unknown.length} permissions
                      </span>
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      {appDetails.report_summary.permissions.unknown.map((perm, index) => (
                        <div key={index} className="bg-gray-800 p-4 rounded-lg border border-purple-900">
                          <div className="flex items-start">
                            <div className="bg-purple-900 p-2 rounded mr-3">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div>
                              <div className="font-medium">{perm.name}</div>
                              <div className="text-sm text-gray-400 mt-1">{perm.info || perm.description}</div>
                              <div className="mt-1 text-xs text-yellow-500">
                                Custom permission defined by the application
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="p-4 bg-gray-800 rounded-lg border border-gray-700">
                  <div className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-400 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm text-gray-300">Permissions determine what your app can access on the Android device. Normal permissions don't pose a risk to user privacy, while dangerous permissions require explicit user approval.</p>
                      <a href="https://developer.android.com/guide/topics/permissions/overview" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 mt-2 inline-block hover:text-blue-300">
                        Learn more about Android permissions
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Exported Components */}
            {appDetails.report_summary && (
              <div className="bg-gray-800 rounded-lg p-6 shadow-lg">
                <h2 className="text-xl font-bold mb-4">Exported Components</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-gray-700 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold">{appDetails.report_summary.exported_activities || 0}</div>
                    <div className="text-sm text-gray-300">Activities</div>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold">{appDetails.report_summary.exported_services || 0}</div>
                    <div className="text-sm text-gray-300">Services</div>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold">{appDetails.report_summary.exported_receivers || 0}</div>
                    <div className="text-sm text-gray-300">Receivers</div>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold">{appDetails.report_summary.exported_providers || 0}</div>
                    <div className="text-sm text-gray-300">Providers</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppAnalysisDetailsView; 